import { Button as MuiButton, SvgIcon, SxProps, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { JSX } from 'react';

export interface ButtonProps {
    text: string;
    onClick: (e: any) => void;
    Icon?: typeof SvgIcon;
    variant?: 'outlined' | 'contained';
    disabled?: boolean;
    color?: 'button' | 'primary' | 'secondary';
    size?: 'small' | 'large';
    sx?: SxProps;
}

export default function Button(props: ButtonProps): JSX.Element {
    const {
        text,
        onClick,
        Icon,
        variant = 'contained',
        disabled = false,
        color = 'button',
        size = 'small',
        sx,
    } = props;
    const theme = useTheme();

    const contrastText = variant === 'contained' ? 'contrastText' : 'main';
    // Light mode: keep secondary[main|contrastText] / primary[...] — preserves
    // tenant-tuned secondary-color text on outlined-secondary buttons.
    // Dark mode + outlined + secondary: secondary.main is typically a dark
    // brand tone that disappears on a dark surface; route the label to
    // text.primary (driven by widgetTextColor) for readability.
    const isDarkOutlinedSecondary =
        theme.palette.mode === 'dark' && variant === 'outlined' && color === 'secondary';
    const chosenColor = isDarkOutlinedSecondary
        ? theme.palette.text.primary
        : color === 'secondary'
          ? theme.palette.secondary[contrastText]
          : theme.palette.primary[contrastText];

    return (
        <MuiButton
            color={color}
            variant={variant}
            disabled={disabled}
            startIcon={Icon ? <Icon /> : undefined}
            onClick={onClick}
            disableElevation
            sx={{
                color: chosenColor,
                fontSize: theme.typography.body1.fontSize,
                padding: size === 'large' ? theme.spacing(1, 4, 1, 4) : undefined,
                minWidth: size === 'large' ? '200px' : undefined,
                // MUI's default outlinedSecondary border is a 0.5-alpha version
                // of secondary.main — invisible on a dark surface when
                // secondary.main itself is a dark brand tone. Override the
                // border (and a soft hover state) only in this exact combo.
                ...(isDarkOutlinedSecondary && {
                    borderColor: alpha(theme.palette.text.primary, 0.4),
                    '&:hover': {
                        borderColor: theme.palette.text.primary,
                        backgroundColor: alpha(theme.palette.text.primary, 0.08),
                    },
                }),
                ...sx,
            }}
        >
            {text}
        </MuiButton>
    );
}
